import { useCallback, useEffect, useRef, useState } from "react";
import isElectron from "../Utils/isElectron";
import isPwa from "../Utils/isPwa";
import {
  forceApplyAppUpdate,
  stripRefreshParams,
} from "../Utils/appUpdateHelpers";
import { APP_UPDATE_AVAILABLE } from "../serviceWorkerRegistration";

const BUILD_INFO_URL = "/build-info.json";
const DISMISSED_KEY = "appUpdateDismissedBuildId";
const POLL_INTERVAL_BROWSER = 5 * 60 * 1000;
const POLL_INTERVAL_PWA = 60 * 1000;

function getCurrentBuildId() {
  return process.env.REACT_APP_BUILD_ID || "dev";
}

function getCurrentVersion() {
  return process.env.REACT_APP_VERSION || "";
}

function getPollInterval() {
  return isPwa() ? POLL_INTERVAL_PWA : POLL_INTERVAL_BROWSER;
}

async function fetchLatestBuildInfo() {
  const res = await fetch(`${BUILD_INFO_URL}?t=${Date.now()}`, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });
  if (!res.ok) return null;
  return res.json();
}

async function probeWaitingWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  await reg.update().catch(() => {});
  return reg;
}

export function useWebAppUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState("");
  const [remoteBuildId, setRemoteBuildId] = useState("");
  const registrationRef = useRef(null);
  const applyingRef = useRef(false);
  const installedPwa = isPwa();

  const currentBuildId = getCurrentBuildId();
  const currentVersion = getCurrentVersion();

  const showUpdate = useCallback(async (detail) => {
    let version = detail?.version || "";
    let buildId = detail?.buildId || "";

    if (!buildId || buildId === getCurrentBuildId()) {
      try {
        const info = await fetchLatestBuildInfo();
        if (info?.buildId && info.buildId !== getCurrentBuildId()) {
          version = info.version || version;
          buildId = info.buildId;
        }
      } catch (_) { /* ignore */ }
    }

    if (!buildId || buildId === getCurrentBuildId()) return;

    // In installed PWAs, always surface updates — no silent dismiss across sessions
    if (!installedPwa) {
      const dismissed = localStorage.getItem(DISMISSED_KEY);
      if (dismissed === buildId) return;
    }

    if (detail?.registration) {
      registrationRef.current = detail.registration;
    } else {
      const reg = await probeWaitingWorker();
      if (reg) registrationRef.current = reg;
    }

    setRemoteVersion(version);
    setRemoteBuildId(buildId);
    setUpdateAvailable(true);
  }, [installedPwa]);

  const checkRemoteVersion = useCallback(async () => {
    if (isElectron() || process.env.NODE_ENV !== "production") return;
    try {
      const info = await fetchLatestBuildInfo();
      if (!info?.buildId) return;
      if (info.buildId !== getCurrentBuildId()) {
        await showUpdate({
          version: info.version,
          buildId: info.buildId,
          source: "build-info",
        });
        return;
      }

      const reg = await probeWaitingWorker();
      if (reg?.waiting) {
        await showUpdate({
          registration: reg,
          version: info.version,
          buildId: info.buildId,
          source: "waiting-worker",
        });
      }
    } catch (error) {
      console.warn("[AppUpdate] Remote version check failed:", error);
    }
  }, [showUpdate]);

  useEffect(() => {
    stripRefreshParams();
  }, []);

  useEffect(() => {
    if (isElectron() || process.env.NODE_ENV !== "production") return undefined;

    const onUpdate = (event) => {
      void showUpdate(event.detail || {});
    };

    const runChecks = () => {
      void checkRemoteVersion();
    };

    window.addEventListener(APP_UPDATE_AVAILABLE, onUpdate);
    runChecks();

    const pollId = setInterval(runChecks, getPollInterval());

    const onVisible = () => {
      if (document.visibilityState === "visible") runChecks();
    };
    const onFocus = () => runChecks();
    const onPageShow = (event) => {
      if (!event.persisted) runChecks();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener(APP_UPDATE_AVAILABLE, onUpdate);
      clearInterval(pollId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [checkRemoteVersion, showUpdate]);

  const applyUpdate = useCallback(async () => {
    if (applyingRef.current) return;
    applyingRef.current = true;
    setIsApplying(true);
    setUpdateAvailable(false);

    try {
      await forceApplyAppUpdate({
        registration: registrationRef.current,
        targetBuildId: remoteBuildId,
      });
    } catch (error) {
      console.error("[AppUpdate] Apply failed:", error);
      applyingRef.current = false;
      setIsApplying(false);
      setUpdateAvailable(true);
    }
  }, [remoteBuildId]);

  const dismissUpdate = useCallback(() => {
    if (installedPwa) return;
    localStorage.setItem(DISMISSED_KEY, remoteBuildId || "unknown");
    setUpdateAvailable(false);
  }, [installedPwa, remoteBuildId]);

  return {
    updateAvailable,
    isApplying,
    isPwa: installedPwa,
    currentVersion,
    currentBuildId,
    remoteVersion,
    remoteBuildId,
    applyUpdate,
    dismissUpdate,
  };
}

export default useWebAppUpdate;
